import React, { useState, useEffect, useCallback } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import DeleteIcon from "@material-ui/icons/Delete";
import { makeStyles } from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { BotaoCancelar } from "../BotoesModal";

const useStyles = makeStyles((theme) => ({
	maxWidth: {
		width: '100%',
	},
	marginTop: {
		marginTop: theme.spacing(2),
	},
	anotacao: {
		display: "block",
		fontSize: "0.75rem",
		opacity: 0.75,
		whiteSpace: "pre-wrap",
		marginTop: 2,
	},
	vazio: {
		textAlign: "center",
		opacity: 0.7,
		padding: theme.spacing(2),
	},
}));

// Aba Tags do lead do ERP dentro de um modal: adicionar (com anotação quando a tag
// pede) e a lista do que o lead já tem, com remover. Aceita leadId ou ticketId —
// o menu de ações da lista só conhece a conversa.
const TicketTagsLead = ({ modalOpen, onClose, leadId, ticketId }) => {
	const [disponiveis, setDisponiveis] = useState([]);
	const [adicionadas, setAdicionadas] = useState([]);
	const [leadResolvido, setLeadResolvido] = useState(null);
	const [selectedTag, setSelectedTag] = useState("");
	const [anotacao, setAnotacao] = useState("");
	const [carregando, setCarregando] = useState(false);
	const [salvando, setSalvando] = useState(false);

	const classes = useStyles();

	const carregar = useCallback(async () => {
		if (!leadId && !ticketId) return;
		setCarregando(true);
		try {
			const url = leadId ? `/tagscrm/${leadId}` : `/tagscrm/ticket/${ticketId}`;
			const { data } = await api.get(url);
			setDisponiveis(data?.disponiveis || []);
			setAdicionadas(data?.adicionadas || []);
			setLeadResolvido(data?.leadId || null);
		} catch (err) {
			setDisponiveis([]);
			setAdicionadas([]);
			setLeadResolvido(null);
			toastError(err);
		}
		setCarregando(false);
	}, [leadId, ticketId]);

	useEffect(() => {
		if (!modalOpen) return;
		setSelectedTag("");
		setAnotacao("");
		carregar();
	}, [modalOpen, carregar]);

	const tagSelecionada = disponiveis.find((t) => t.id === selectedTag);

	const handleAdicionar = async () => {
		if (!selectedTag) {
			toast.warning("Tag não selecionada.");
			return;
		}
		// Mesma regra da aba Tags do CRM; o ERP também recusa, isto é só para não ir à toa.
		if (tagSelecionada?.solicitarAnotacao && !anotacao.trim()) {
			toast.warning("Para adicionar a tag selecionada, a anotação deve ser preenchida.");
			return;
		}
		setSalvando(true);
		try {
			await api.post(`/adicionartagcrmlead`, {
				leadId: leadResolvido,
				tagCrmId: selectedTag,
				anotacaoTag: anotacao,
			});
			setSelectedTag("");
			setAnotacao("");
			await carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const handleRemover = async (leadTag) => {
		setSalvando(true);
		try {
			await api.post(`/removertagcrmlead`, {
				leadId: leadResolvido,
				leadTagId: leadTag.id,
			});
			await carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	return (
		<Dialog open={modalOpen} onClose={onClose} fullWidth maxWidth="sm" scroll="paper">
			<DialogTitle id="form-dialog-title">Tags do lead</DialogTitle>
			<DialogContent dividers>
				<FormControl variant="outlined" className={classes.maxWidth}>
					<InputLabel>Tag</InputLabel>
					<Select
						value={selectedTag}
						onChange={(e) => {
							setSelectedTag(e.target.value);
							setAnotacao("");
						}}
						label="Tag"
						disabled={carregando || !leadResolvido}
					>
						<MenuItem value={''}>&nbsp;</MenuItem>
						{disponiveis.map((tag) => (
							<MenuItem key={tag.id} value={tag.id}>{tag.nome}</MenuItem>
						))}
					</Select>
				</FormControl>

				{tagSelecionada?.solicitarAnotacao && (
					<FormControl variant="outlined" className={`${classes.maxWidth} ${classes.marginTop}`}>
						<TextField
							label="Digite a anotação"
							variant="outlined"
							multiline
							rows={4}
							fullWidth
							required
							value={anotacao}
							onChange={(e) => setAnotacao(e.target.value)}
						/>
					</FormControl>
				)}

				<ButtonWithSpinner
					className={classes.marginTop}
					variant="contained"
					color="primary"
					fullWidth
					loading={salvando}
					disabled={carregando || !leadResolvido}
					onClick={handleAdicionar}
				>
					Adicionar tag
				</ButtonWithSpinner>

				<Table size="small" className={classes.marginTop}>
					<TableHead>
						<TableRow>
							<TableCell>TAG</TableCell>
							<TableCell align="center" style={{ width: 80 }}>OPÇÕES</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{adicionadas.map((leadTag) => (
							<TableRow key={leadTag.id}>
								<TableCell>
									{leadTag.nome}
									{leadTag.anotacao && (
										<Typography component="span" className={classes.anotacao}>
											{leadTag.anotacao}
										</Typography>
									)}
								</TableCell>
								<TableCell align="center">
									<IconButton
										size="small"
										title="Remover tag"
										disabled={salvando}
										onClick={() => handleRemover(leadTag)}
									>
										<DeleteIcon fontSize="small" color="error" />
									</IconButton>
								</TableCell>
							</TableRow>
						))}
						{adicionadas.length === 0 && (
							<TableRow>
								<TableCell colSpan={2} className={classes.vazio}>
									{carregando ? "Carregando..." : "Não há nenhuma tag adicionada no lead"}
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</DialogContent>
			<DialogActions>
				<BotaoCancelar onClick={onClose} disabled={salvando}>
					Fechar
				</BotaoCancelar>
			</DialogActions>
		</Dialog>
	);
};

export default TicketTagsLead;
